<script>
    import DeleteResourceModal from '@/components/Modals/DeleteResourceModal.vue';
    import Loader from '@/components/Icons/Loader.vue';
    import DrawablePhoto from '@/components/DrawablePhoto/DrawablePhoto.vue';
    import Heading from '@/components/Heading.vue';
    import PhotoReader from '@/util/PhotoReader';
    import { eventBus } from '@/eventBus';
    import uuidv4 from '@/util/uuidv4';

    export default {
        name: 'AddPhotos',
        components: {
            DrawablePhoto,
            Loader,
            DeleteResourceModal,
            Heading,
        },
        props: {
            value: {
                type: Array,
                required: true,
                default: () => [],
            },
        },

        data: () => ({
            isLoading: false,
            photoUuidToDelete: false,
        }),

        methods: {
            deletePhoto(uuid) {
                const newPhotos = this.value.filter((photo) => photo.uuid !== uuid);
                this.$emit('input', newPhotos);
                this.photoUuidToDelete = false;
            },

            updatePhoto(uuid, drawing) {
                const newPhotos = this.value.map((photo) => {
                    if (photo.uuid !== uuid) return photo;
                    return {
                        ...photo,
                        drawing,
                        uuid: uuidv4(), // we must provide a new uuid to force the v-for to re-render with the new drawing
                    };
                });
                this.$emit('input', newPhotos);
            },

            async addPhoto(event) {
                if (!event.target.files) return;
                this.isLoading = true;

                const originalFiles = this.value;
                const placeholderFiles = Array(event.target.files.length)
                    .fill({
                        loading: true,
                        image: null,
                        width: null,
                        height: null,
                        drawing: null,
                        thumbnail: null,
                    })
                    .map((placeholder) => ({ ...placeholder, uuid: uuidv4() }));

                this.$emit('input', [...this.value, ...placeholderFiles]);

                const processedFiles = [];
                // eslint-disable-next-line no-restricted-syntax
                for (const file of event.target.files) {
                    try {
                        const {
                            photo,
                            thumbnail,
                            width,
                            height,
                            // eslint-disable-next-line no-await-in-loop
                        } = await PhotoReader.read(file);
                        processedFiles.push({
                            image: photo,
                            thumbnail,
                            width,
                            height,
                            uuid: uuidv4(),
                        });
                    } catch (error) {
                        const errorMessage = `${this.$t(
                            'Error loading image: ',
                        )} ${error}`;
                        eventBus.$emit('error', errorMessage);
                    }
                }

                this.$emit('input', [...originalFiles, ...processedFiles]);

                this.$refs.fileInput.value = null;
                this.isLoading = false;
            },
        },

        computed: {
            hasPhotos() {
                return this.value && this.value.length > 0;
            },
        },
    };
</script>

<template>
    <div>
        <DeleteResourceModal
            v-if="photoUuidToDelete"
            mode="delete"
            @confirm="deletePhoto(photoUuidToDelete)"
            @close="photoUuidToDelete = false"
        >
            <div class="p-8">
                <Heading :level="2" class="mb-6">
                    {{ $t('Delete Photo') }}
                </Heading>
                <p class="text-80 leading-normal">
                    {{ $t('Are you sure you want to delete this photo?') }}
                </p>
            </div>
        </DeleteResourceModal>

        <div v-if="hasPhotos" class="photos-container mb-4">
            <DrawablePhoto
                v-for="photo in value"
                photoClass="mr-0 mb-0"
                :key="photo.uuid"
                :image="photo.image"
                :drawing="photo.drawing"
                :thumbnail="photo.thumbnail"
                :width="photo.width"
                :height="photo.height"
                :loading="photo.loading"
                @change="updatePhoto(photo.uuid, arguments[0])"
                @delete="photoUuidToDelete = photo.uuid"
                small
            />
        </div>

        <label class="btn btn-default text-primary px-0">
            <input
                class="hidden"
                type="file"
                accept="image/*"
                :disabled="isLoading"
                @change="addPhoto"
                multiple
                ref="fileInput"
            />
            <div :disabled="isLoading">
                <Loader v-if="isLoading" />
                <template v-else>
                    <span>+ {{ $t('Add photo') }}</span>
                </template>
            </div>
        </label>
    </div>
</template>

<style scoped>
    label {
        height: 23px;
        line-height: normal;
    }

    .photos-container {
        display: grid;
        grid-template-columns: repeat(auto-fill, 160px);
        grid-gap: 1.5rem 1rem;
    }

    .photos-container > div {
        margin-right: 0;
        margin-bottom: 0;
    }
</style>
